import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const Experience = () => {
  const sectionRef = useRef();
  const lineRef = useRef();
  const labelRef = useRef();
  const headingRef = useRef();
  const experienceItemsRef = useRef([]);
  const lettersRef = useRef([]);

  useEffect(() => {
    // Animation de la ligne
    gsap.fromTo(lineRef.current,
      { scaleX: 0, opacity: 0 },
      {
        scaleX: 1,
        opacity: 1,
        duration: 1.5,
        ease: "power3.out",
        scrollTrigger: {
          trigger: lineRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation du label
    gsap.fromTo(labelRef.current,
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        delay: 0.3,
        ease: "power2.out",
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation du texte split
    const headingWords = headingRef.current.querySelectorAll('.word');
    gsap.fromTo(headingWords,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation des items d'expérience
    const experienceItems = experienceItemsRef.current;
    experienceItems.forEach((item, index) => {
      if (item) {
        gsap.fromTo(item,
          { 
            y: 80, 
            opacity: 0,
            scale: 0.9
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 1,
            delay: index * 0.2,
            ease: "power3.out",
            scrollTrigger: {
              trigger: item,
              start: "top 90%",
              toggleActions: "play none none reverse"
            }
          }
        );

        // Hover effect sur les images
        const img = item.querySelector('.experience-img');
        if (img) {
          item.addEventListener('mouseenter', () => {
            gsap.to(img, {
              scale: 1.05,
              duration: 0.6,
              ease: "power2.out"
            });
          });

          item.addEventListener('mouseleave', () => {
            gsap.to(img, {
              scale: 1,
              duration: 0.6,
              ease: "power2.out"
            });
          });
        }
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addToExperienceRef = (el) => {
    if (el && !experienceItemsRef.current.includes(el)) {
      experienceItemsRef.current.push(el);
    }
  };

  const addToLettersRef = (el) => {
    if (el && !lettersRef.current.includes(el)) {
      lettersRef.current.push(el);
    }
  };

  // Split text en mots pour l'animation
  const headingText = "With a versatile career spanning music, television, and fashion, I bring authentic storytelling and artistic expression to every project.";
  const words = headingText.split(' ');

  // Données des expériences
  const experiences = [
    {
      id: 1,
      title: "Singer",
      image: "/assets/17.jpg",
      alt: "Safaa Shiey performing live on stage",
      description: "Versatile vocalist performing across events, fashion shows, and international stages",
      bullets: [
        "Semi-finalist on Spain's Got Talent and featured on TV show Fama a Bailar",
        "Performed at prestigious events including Nelson Mandela Gala (Cannes) and King of Morocco’s 25th Coronation Anniversary",
        "Showcased Soul, R&B, and Pop in multiple countries: UK, Italy, France, Lebanon, Turkey, and Morocco",
        "Regular house vocalist in clubs and private events across Spain, including Grupo Paraguas restaurants and Ibiza parties"
      ]
    },
    {
      id: 2,
      title: "Actress",
      image: "/assets/13.jpg",
      alt: "Safaa Shiey in television drama role",
      description: "Dynamic and versatile performer across film, TV, and music videos",
      bullets: [
        "Lead in Fran Dieli Disco Collaboration music video “Chocolate and Vanilla”",
        "Portrayed Nadia in TV series “El Príncipe”",
        "Featured in the film “Santiago Apóstol”",
        "Singer role in Xiaomi commercial spot"
      ]
    },
    {
      id: 3,
      title: "Model",
      image: "/assets/1.jpg",
      alt: "Safaa Shiey modeling for fashion campaign",
      description: "Elegant fashion and brand representation in international campaigns",
      bullets: [
        "Featured in Ted Baker international campaign",
        "Appeared in music video and promotional projects for major artists",
        "Collaborated on commercial and creative campaigns",
        "Versatile presence across photo and video shoots"
      ]
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="section_experience relative bg-white py-20 lg:py-32 overflow-hidden"
    >
      {/* Espacement initial */}
      <div className="padding-section-medium h-16 lg:h-24" id="experience" />

      {/* Ligne décorative */}
      <div className="px-6 lg:px-8">
        <div 
          ref={lineRef}
          className="line w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent max-w-7xl mx-auto"
        />
      </div>

      <div className="padding-section-medium h-16 lg:h-24" />

      {/* Contenu principal */}
      <div className="px-6 lg:px-8">
        <div className="container-medium max-w-7xl mx-auto">
          
          {/* Grid principale */}
          <div className="experience_component wf-grid grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Colonne de contenu */}
            <div className="experience_content lg:col-span-5">
              <div 
                ref={labelRef}
                className="label_wrap flex items-center space-x-4 mb-8"
              >
                <div className="label_line w-12 h-px bg-gradient-to-r from-purple-400 to-pink-400" />
                <h2 className="text-style-label text-sm font-bold text-gray-700 tracking-widest uppercase">
                  Experience
                </h2>
              </div>

              <div className="spacer-large h-8" />

              {/* Heading avec animation split */}
              <h2 
                ref={headingRef}
                className="heading-style-h3 text-3xl lg:text-4xl xl:text-5xl font-light text-gray-600 leading-tight"
              >
                {words.map((word, index) => (
                  <span key={index} className="word inline-block mr-1">
                    {word}
                  </span>
                ))}
              </h2>
            </div>

            {/* Colonne des items d'expérience */}
            <div className="experience_items lg:col-span-7 space-y-12">
              {experiences.map((experience) => (
                <div
                  key={experience.id}
                  ref={addToExperienceRef}
                  className="experience_item group cursor-pointer"
                >
                  <div className="experience_img-wrap relative overflow-hidden rounded-2xl">
                    <img
                      src={experience.image}
                      alt={experience.alt}
                      loading="lazy"
                      className="experience_img w-full h-64 lg:h-80 object-cover object-[50%_25%] transition-transform duration-700 group-hover:scale-105"
                    />
                    
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>

                  <div className="experience_item-infos mt-6">
                    <h3 className="heading-style-h6 text-2xl lg:text-3xl font-semibold text-gray-900 mb-2">
                      {experience.title}
                    </h3>
                    <div className="services_desc text-lg text-gray-600 mb-4">
                      {experience.description}
                    </div>

                    {/* Liste des bullet points */}
                    <ul className="space-y-3">
                      {experience.bullets.map((bullet, index) => (
                        <li 
                          key={index}
                          className="flex items-start space-x-3 text-gray-700 leading-relaxed"
                        >
                          <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm lg:text-base">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      {/* Espacement final */}
      <div className="padding-section-xsmall h-12 lg:h-16" />

      <style jsx>{`
        .section_experience {
          perspective: 1000px;
        }

        .experience_img {
          transform-origin: center;
        }

        .word {
          transform-origin: center bottom;
          backface-visibility: hidden;
        }

        .experience_item {
          transform-style: preserve-3d;
        }

        @media (max-width: 1024px) {
          .experience_component {
            gap: 8;
          }
        }

        @media (max-width: 768px) {
          .heading-style-h3 {
            font-size: 2rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Experience;